<script>
import { mapState, mapGetters } from 'vuex';

export default {
  name: 'GoogleMapsDataLayer',

  props: {
    /**
     * Get regions from https://cartomap.github.io/nl/wgs84/gemeente_2020.geojson
     */
    json: {
      type: String,
      required: true,
    },

    downloadable: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      selectedRegion: null,
      states: {
        '0,25000': 'small',
        '25000,50000': 'medium',
        '50000,100000': 'normal',
        '100000,20000000': 'large',
      },
    };
  },

  computed: {
    ...mapState({
      google: (state) => state.google.api,
      map: (state) => state.google.map,
      selected: (state) => state.dataLayer.selected,
      featureClicked: (state) => state.dataLayer.clicked,
      selectedListItem: (state) => state.list.selected,
      counterItem: (state) => state.counter.active,
      infoBoxCounterItem: (state) => state.infoBoxCounter.activeItem,
      counterIsRunning: (state) => state.infoBoxCounter.isRunning,
      selectedYear: (state) => state.rawData.year,
      storedFacets: (state) => state.filter.facets,
    }),

    ...mapGetters({
      data: 'rawData/separatedData',
      statedFilters: 'filter/filters',
    }),
  },

  watch: {
    storedFacets() {
      this.setStyle();
    },

    counterItem(value) {
      if (value !== null) {
        let dlFeature = null;

        this.map.data.forEach((feature) => {
          if (feature.getProperty('statnaam') === value.Gemeente) {
            dlFeature = feature;
          }
        });

        this.$store.commit('dataLayer/setActiveFeature', dlFeature);
      }
    },

    infoBoxCounterItem(value) {
      if (value === null) {
        return;
      }

      if (Array.isArray(value)) {
        value.forEach((item) => {
          this.map.data.forEach((feature) => {
            if (feature.getProperty('statnaam') === item.Gemeente) {
              this.setProperties(feature, true);
            }
          });
        });

        return;
      }

      this.map.data.forEach((feature) => {
        if (feature.getProperty('statnaam') === value.Gemeente) {
          this.setProperties(feature, true);
        }
      });
    },

    selected(value) {
      if (value === null) {
        this.selectedRegion = null;
        this.setStyle();
      }
    },

    selectedYear() {
      this.selectedRegion = null;
      this.$store.commit('dataLayer/setSelectedRegion', this.selectedRegion);
      // this.$store.commit('counter/start');

      this.setStyle();
    },

    statedFilters() {
      this.selectedRegion = null;
      this.$store.commit('dataLayer/setSelectedRegion', this.selectedRegion);
      // this.$store.commit('counter/start');

      this.setStyle();

      if (this.downloadable) {
        setTimeout(() => {
          this.saveDownload();
        }, 1000);
      }
    },

    selectedListItem(value) {
      if (value === null) {
        this.selectedRegion = null;
        this.$store.commit('dataLayer/setSelectedRegion', this.selectedRegion);

        return;
      }

      const { Gemeente } = this.selectedListItem;

      this.selectedRegion = Gemeente;

      this.setStyle();

      let dlFeature = null;

      this.map.data.forEach((feature) => {
        if (feature.getProperty('statnaam') === this.selectedRegion) {
          dlFeature = feature;
        }
      });

      this.$store.commit('dataLayer/setSelectedRegion', this.selectedRegion);
      this.$store.commit('dataLayer/setFeature', dlFeature);
    },
  },

  mounted() {
    this.map.data.loadGeoJson(
      this.json,
      {},
      (features) => {
        features.forEach((feature) => {
          this.setProperties(feature);
        });

        if (this.downloadable) {
          setTimeout(() => {
            this.saveDownload();
          }, 1000);
        }
      },
    );

    this.setStyle();

    this.map.data.addListener('mouseover', (event) => {
      if (this.featureClicked) {
        return;
      }

      const { feature } = event;
      const state = this.states[this.statedFilters.Inwoners.value[0]];

      if (feature.getProperty('type') !== state) {
        this.$store.commit('dataLayer/setHover', false);
        this.$store.commit('dataLayer/setActiveFeature', null);
        return;
      }

      // this.$store.commit('counter/stop');
      this.$store.commit('dataLayer/setHover', true);
      this.$store.commit('dataLayer/setActiveFeature', feature);
    });

    this.map.data.addListener('mouseout', () => {
      if (this.featureClicked) {
        return;
      }

      // this.$store.commit('counter/start');
      this.$store.commit('dataLayer/setHover', false);
    });

    this.map.data.addListener('click', (event) => {
      const { feature } = event;
      const state = this.states[this.statedFilters.Inwoners.value[0]];

      if (feature.getProperty('type') !== state
        || feature.getProperty('statnaam') === this.selectedRegion) {
        this.$store.commit('dataLayer/setHover', false);
        this.$store.commit('dataLayer/isClicked', false);
        this.$store.commit('dataLayer/deselect');
        this.$store.commit('counter/start');

        this.setStyle();
        return;
      }

      this.$store.commit('dataLayer/isClicked', true);

      this.$store.commit('counter/stop');

      if (this.counterIsRunning) {
        this.$store.commit('infoBoxCounter/stop');
        this.resetState(true);
      }

      feature.setProperty('active', true);

      this.selectedRegion = feature.getProperty('statnaam');
      this.setStyle();

      this.$store.commit('dataLayer/setSelectedRegion', feature.getProperty('statnaam'));
      this.$store.commit('dataLayer/setFeature', feature);
      this.$store.commit('dataLayer/setEvent', event);
    });

    this.$root.$on('dataLayer/reset', () => {
      this.resetState(false);
    });

    this.$root.$on('dataLayer/set', () => {
      this.resetState(true);
    });
  },

  methods: {
    getPosition(region) {
      const obj = {
        position: null,
        color: null,
        type: null,
      };

      if (this.data) {
        Object.keys(this.data).forEach((type) => {
          const el = this.data[type].filter((item) => item.Gemeente === region);
          const index = this.data[type].findIndex((item) => item.Gemeente === region);
          const color = el[0]?.colors;
          const rapport = el[0]?.Rapportcijfer;
          const mutation = el[0]?.Positie;

          if (index > -1) {
            obj.position = index;
            obj.color = color;
            obj.type = type;
            obj.rapport = rapport;
            obj.mutation = mutation;
          }
        });
      }

      return obj;
    },

    getCenter(feature) {
      const bounds = new this.google.maps.LatLngBounds();
      const coords = feature.getGeometry();

      coords.forEachLatLng((coord) => {
        bounds.extend(coord);
      });

      return bounds.getCenter();
    },

    setProperties(feature, setColor = false) {
      const {
        type,
        color,
        rapport,
        mutation,
      } = this.getPosition(feature.getProperty('statnaam'));
      const center = this.getCenter(feature);

      if (this.data && this.data[type]) {
        // const listLength = this.data[type].length;
        // const switchNumbers = ((position) - listLength) * -1;
        // const percentage = (100 / listLength) * switchNumbers;

        feature.setProperty('type', type);
        // feature.setProperty('opacity', (0.5 / 100) * percentage + 0.5);
        feature.setProperty('fillColor', setColor ? color : '#FFFFFF');
        feature.setProperty('rapport', rapport);
        feature.setProperty('mutation', mutation);
      }

      feature.setProperty('center', center);
      feature.setProperty('strokeColor', '#FFFFFF');
      feature.setProperty('strokeWeight', 1);
    },

    setStyle() {
      const state = this.states[this.statedFilters.Inwoners.value[0]];

      this.map.data.setStyle((feature) => {
        const styles = {
          fillColor: '#FFFFFF',
          strokeColor: '#C5C5C5',
          strokeWeight: 1,
          zIndex: 1,
          fillOpacity: 1,
        };
        const type = feature.getProperty('type');

        let isValid = true;

        if (Object.keys(this.storedFacets).length) {
          Object.keys(this.storedFacets).forEach((facet) => {
            if (!isValid) {
              return;
            }

            if (this.storedFacets[facet] === null) {
              isValid = true;

              return;
            }

            if (facet === 'rapport') {
              if (
                feature.getProperty('rapport') > this.storedFacets[facet][0]
                && feature.getProperty('rapport') < this.storedFacets[facet][1]
              ) {
                isValid = true;

                return;
              }
            }

            if (facet === 'position') {
              if (feature.getProperty('mutation') === this.storedFacets[facet]) {
                isValid = true;

                return;
              }
            }

            isValid = false;
          });
        }

        if (state === type && isValid) {
          styles.fillColor = feature.getProperty('fillColor');
          styles.fillOpacity = 1;
        }

        if (
          this.selectedRegion === feature.getProperty('statnaam')
          && type === state
          && isValid
        ) {
          styles.fillColor = '#00A4DF';
          styles.strokeColor = '#006387';
          styles.strokeWeight = 4;
          styles.zIndex = 3;
          styles.fillOpacity = 1;
        }

        return (styles);
      });
    },

    resetState(state) {
      this.map.data.forEach((feature) => {
        this.setProperties(feature, state);
      });
    },

    saveDownload() {
      const geoJSON = [];

      fetch(this.json)
        .then((response) => response.json())
        .then((data) => {
          this.map.data.forEach((feature) => {
            const region = feature.getProperty('statnaam');
            const { features } = data;
            const item = features.filter((f) => f.properties.statnaam === region);

            item[0].properties.type = feature.getProperty('type');

            let opacity = 0.4;

            if (item[0].properties.type === this.states[this.statedFilters.Inwoners.value[0]]) {
              opacity = feature.getProperty('opacity');
            }

            item[0].style = {
              fillColor: feature.getProperty('fillColor'),
              strokeColor: feature.getProperty('strokeColor'),
              strokeWeight: feature.getProperty('strokeWeight'),
              fillOpacity: opacity,
            };

            geoJSON.push(item[0]);
          });
        });

      this.$store.commit('dataLayer/setDownload', geoJSON);
    },
  },

  render() {
    return null;
  },
};
</script>
